import { DataProvider, Identifier } from "react-admin";
import { FieldValues } from "react-hook-form";
import { FinlandGroupTypeEnum, FranceGroupTypeEnum, SiteContryEnum } from "shared/enums/globalEnums";
import { fetchConfigsQuery, fetchPlanningMasksForSpecificDateQuery, fetchSelectedGroupsQuery } from "./create/queries";
import { GroupSiteAssociationFormValues, ValidationErrors } from "./types";

const getEffectiveGroupIds = (values: GroupSiteAssociationFormValues): Identifier[] => {
  if (values.group_ids && values.group_ids.length > 0) return values.group_ids;
  if (values.group_id !== undefined && values.group_id !== null) return [values.group_id];
  return [];
};

const getGroupSelectionState = (values: GroupSiteAssociationFormValues) => {
  const hasGroupId = values.group_id !== undefined && values.group_id !== null && values.group_id !== "";
  const hasGroupIds = Array.isArray(values.group_ids) && values.group_ids.length > 0;
  return { hasGroupId, hasGroupIds };
};

const validateRequiredFields = (values: GroupSiteAssociationFormValues, errors: ValidationErrors) => {
  const { hasGroupId, hasGroupIds } = getGroupSelectionState(values);

  if (!hasGroupId && !hasGroupIds) {
    errors.group_id = "Required";
    errors.group_ids = "Required";
  }

  // Finland flow uses groupType before loading group options.
  if (values.group_id !== undefined && !values.groupType) {
    errors.groupType = "Required";
  }

  if (!Array.isArray(values.site_ids) || values.site_ids.length === 0) {
    errors.site_ids = "Required";
  }

  if (!values.start_date) {
    errors.start_date = "Required";
  }
};

export const validateDateRange = (values: GroupSiteAssociationFormValues, errors: ValidationErrors) => {
  if (!values.start_date || !values.end_date) return;

  const startDate = new Date(values.start_date);
  const endDate = new Date(values.end_date);

  if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && startDate >= endDate) {
    errors.end_date = "End date must be after start date";
  }

  // end date must be after today
  if (!Number.isNaN(endDate.getTime()) && endDate < new Date()) {
    errors.end_date = "End date must be in the future";
  }
};

const validateFranceEdpPlanningMasks = async ({
  values,
  errors,
  dataprovider,
  country,
}: {
  values: GroupSiteAssociationFormValues;
  errors: ValidationErrors;
  dataprovider: DataProvider;
  country: SiteContryEnum;
}) => {
  const { hasGroupId, hasGroupIds } = getGroupSelectionState(values);
  const hasSites = Array.isArray(values.site_ids) && values.site_ids.length > 0;
  const shouldRunEdpChecks = country === SiteContryEnum.France && (hasGroupIds || hasGroupId) && hasSites;

  if (!shouldRunEdpChecks) return;

  try {
    const startDate = values.start_date;
    if (!startDate) return;

    const groupIds = getEffectiveGroupIds(values);
    if (groupIds.length === 0) return;

    const groups = await fetchSelectedGroupsQuery(dataprovider, groupIds);
    const hasRteEdp = groups.some((group) => String(group.type) === FranceGroupTypeEnum.rte_edp);
    if (!hasRteEdp) return;

    const planningMasks = await fetchPlanningMasksForSpecificDateQuery(values.site_ids, startDate);
    const siteIdsWithoutPlanningMasks = values.site_ids.filter((siteId) => {
      return !planningMasks.some((mask) => mask.site_id === Number(siteId));
    });
    if (siteIdsWithoutPlanningMasks.length > 0) {
      errors.site_ids = `Some sites do not have a planning mask for the selected start date and cannot be associated to an EDP: ${siteIdsWithoutPlanningMasks.join(
        ", ",
      )}`;
    }
  } catch {
    errors.site_ids = "Unable to validate RTE EDP planning masks. Please try again.";
  }
};

const validateFinlandPeHasConfigs = async ({
  values,
  errors,
  dataprovider,
  country,
}: {
  values: GroupSiteAssociationFormValues;
  errors: ValidationErrors;
  dataprovider: DataProvider;
  country: SiteContryEnum;
}) => {
  if (country !== SiteContryEnum.Finland) return;
  if (!values.group_id) return;

  try {
    const groups = await fetchSelectedGroupsQuery(dataprovider, [values.group_id]);
    const selectedGroup = groups[0];
    if (!selectedGroup || String(selectedGroup.type) !== FinlandGroupTypeEnum.fingrid_pe) return;

    const configs = await fetchConfigsQuery(dataprovider, selectedGroup.code);
    if (configs.length === 0) {
      errors.group_id = "No configurations found for this PE.";
    }
  } catch {
    errors.group_id = "Unable to validate Finland PE configurations. Please try again.";
  }
};

export const validateCreateForm =
  ({ dataprovider, country }: { dataprovider: DataProvider; country: SiteContryEnum }) =>
  async (formValues: FieldValues) => {
    const values = formValues as GroupSiteAssociationFormValues;
    const errors: ValidationErrors = {};

    validateRequiredFields(values, errors);
    validateDateRange(values, errors);
    if (country === SiteContryEnum.France) {
      await validateFranceEdpPlanningMasks({ values, errors, dataprovider, country });
    }
    if (country === SiteContryEnum.Finland) {
      await validateFinlandPeHasConfigs({ values, errors, dataprovider, country });
    }

    return errors;
  };

export const validateEditForm = (formValues: FieldValues) => {
  const values = formValues as GroupSiteAssociationFormValues;
  const errors: ValidationErrors = {};

  validateDateRange(values, errors);

  return errors;
};
